
import { Component, inject, input, output, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { DatePicker } from 'primeng/datepicker';
import { Dialog } from 'primeng/dialog';
import { FileUpload, FileUploadHandlerEvent } from 'primeng/fileupload';
import { FloatLabel } from 'primeng/floatlabel';
import { FormHealthRecordDocument, HealthRecordDocumentUploadMetadata, StoredHealthRecordDocument } from '@web/csl-lib-models';
import { FormsModule, NgForm } from '@angular/forms';
import { HealthRecordDocumentsService } from '@web/csl-lib-services';
import { HealthRecordDocumentTypesConstants, StorageDestination } from '@web/csl-lib-constants';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { PatientBannerComponent } from '@web/csl-lib-components';
import { TextareaModule } from 'primeng/textarea';

@Component({
  selector: 'csl-scp-upload-dialog',
  standalone: true,
  imports: [
    DatePicker,
    Dialog,
    FileUpload,
    FloatLabel,
    FormsModule,
    PatientBannerComponent,
    TextareaModule
],
  templateUrl: './scp-upload-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './scp-upload-dialog.component.css',
})
export class ScpUploadDialogComponent {
  uploadForm = viewChild<NgForm>('uploadForm');
  fileUploader = viewChild<FileUpload>('fu');

  /* Inputs */

  patientLastFirstName = input<string>();
  patientDob = input<Date>();
  healthRecordId = input<string>();
  minDate = input.required<Date>();
  maxDate = input.required<Date>();
  visible = input<boolean>();
  clinicId = input.required<number>();

  /* Outputs */

  onUploadComplete = output<StoredHealthRecordDocument>();
  onHide = output<void>();

  /* Store */


  /* Services */

  docService = inject(HealthRecordDocumentsService);
  messageService = inject(MessageService);

  /* Member Vars */

  formDocument = new FormHealthRecordDocument();
  uploadCount = 0;

  /* Lifecycle Hooks */


  /* Event Handlers */

  onHideDialog() {
    this.onHide.emit();
  }

  onUpload(event: FileUploadHandlerEvent) {
    const file = event.files[0];

    this.formDocument = {
      ...this.formDocument,
      documentType:
        HealthRecordDocumentTypesConstants.DOCTYPE_SURVIVOR_CARE_PLAN,
      fileName: file.name,
      fileSizeBytes: file.size,
      fileDate: new Date(file.lastModified),
    };

    const metadata: HealthRecordDocumentUploadMetadata = {
      // Document
      documentDate: this.formDocument.documentDate,
      documentType: this.formDocument.documentType,
      comment: this.formDocument.comment,

      // Storage Destination
      storageDestination: StorageDestination.HealthRecordDocuments,
    };

    this.docService
      .upload(this.healthRecordId()!, file, metadata, this.clinicId())
      .subscribe({
        next: (doc) => {
          this.onUploadComplete.emit(doc);
          // Increment count of documents uploaded; the worklist will be refreshed only when the dialog is closed and if count > 0
          this.uploadCount++;
          setTimeout(() => {
            this.uploadForm()!.resetForm();
            this.clearFileUploader();
          }, 1500);
        },
        error: (error: HttpErrorResponse) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Upload Failed',
            detail: error.error ?? 'An error occurred while uploading the document.',
            life: 5000,
          });
          this.clearFileUploader();
        },
      });
  }

  /* Instance Methods */

  clearFileUploader() {
    const uploader = this.fileUploader()!;
    uploader.clear();
    // PrimeNG's FileUpload.clear() does not reset uploadedFileCount (a v20->v22 regression), so with
    // fileLimit=1 and customUpload, the Browse button stays permanently disabled after the first
    // successful upload unless we reset it here ourselves.
    uploader.uploadedFileCount = 0;
  }
}
